import { useMemo, useState } from "react";
import styled from "styled-components";
import { Outlet, useNavigate } from "react-router-dom";

import { FunctionGroup } from "./FunctionGroup";
import { JobsListEmptyState } from "./JobsListEmptyState";
import { useJobsList } from "./useJobsList";
import { Filters } from "./Filters";
import { JobPreview } from "./JobPreview";
import { JobCounts, LiveJobCounts } from "./JobCounts";
import { CoreJobList } from "./CoreJobList";
import { MissingSalaryWarning } from "./MissingSalaryWarning";

import { Feature, JobPreviewFragment } from "@hire/schema";
import { Loading } from "@otta/shared-components";
import { modularScale, palette, pxToRem } from "@otta/design-tokens";
import { Button, Spacing, Text, media } from "@otta/design";
import { alphabeticallySortKeys } from "@hire/util/collections";
import { useAuthorizations } from "@hire/providers/authorization";
import { RemovedJobsExplanation } from "@hire/components/RemovedJobsModal";
import { ReloadableError } from "@hire/components/ReloadableError";
import { PausedProUpsellPage } from "@hire/components/ProUpsell";
import { Icon } from "@otta/icons";

const JobsPageContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  ${media({ from: "tablet" })`
    flex-direction: row;
    max-height: 100%; 
  `};
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const JobsListContainer = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: ${modularScale(1)};
`;

const TopSectionContainer = styled.div`
  background: ${palette.brand.white};
  padding: ${modularScale(1)};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LeftSideWrapper = styled.div`
  display: flex;
  gap: ${pxToRem(40)};
  width: 100%;
`;

const JobList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${modularScale()};
  align-items: stretch;
`;

const CreateJobButton = styled(Button)`
  display: flex;
  gap: ${pxToRem(8)};
  padding: ${pxToRem(8)} ${pxToRem(24)};
`;

function InnerJobsList({
  jobs,
  review,
  isPublished,
}: {
  jobs: JobPreviewFragment[];
  review: boolean;
  isPublished: boolean;
}) {
  const jobsGroupedByFunction = useMemo(() => {
    const grouped = jobs.reduce<Record<string, JobPreviewFragment[]>>(
      (acc, job) => {
        const functionName = job.jobFunction?.value ?? "Unknown";

        if (!acc[functionName]) {
          acc[functionName] = [];
        }

        acc[functionName].push(job);

        return acc;
      },
      {}
    );

    return alphabeticallySortKeys(grouped);
  }, [jobs]);

  const showMissingSalaryWarning =
    isPublished && jobs.some(job => !job.salaryRange);

  return (
    <Spacing size={2}>
      {review && (
        <Text bold>
          We are checking that these jobs align with the locations and job
          functions we support on Otta. This can take up to 2 working days.
        </Text>
      )}

      {showMissingSalaryWarning && <MissingSalaryWarning />}

      {Object.entries(jobsGroupedByFunction).map(([functionName, jobs]) => (
        <FunctionGroup
          key={functionName}
          name={functionName}
          total={jobs.length}
        >
          <JobList>
            {jobs.map(j => (
              <JobPreview
                key={j.id}
                {...j}
                review={review}
                isPublished={isPublished}
              />
            ))}
          </JobList>
        </FunctionGroup>
      ))}
    </Spacing>
  );
}

export const JobsList = () => {
  const navigate = useNavigate();
  const [showPausedModal, setShowPausedModal] = useState(false);
  const {
    updateSearchParams,
    loading,
    error,
    company,
    jobs = [],
    status,
    location,
    refetch,
  } = useJobsList();

  const { features, loading: featuresLoading } = useAuthorizations([
    Feature.CreateJob,
    Feature.ViewJobs,
    Feature.JobSlots,
  ]);

  const createJobFeature = features.get(Feature.CreateJob);
  const viewJobsFeature = features.get(Feature.ViewJobs);
  const jobSlotsFeature = features.get(Feature.JobSlots);

  return (
    <>
      <JobsPageContainer data-testid="jobs-list-container">
        <Filters
          status={status}
          updateSearchParams={updateSearchParams}
          location={location}
        />

        <FlexContainer>
          <TopSectionContainer>
            <LeftSideWrapper>
              {company &&
                (status === "published" ? (
                  <LiveJobCounts
                    visibleCount={company.totalJobs}
                    showPausedModal={() => setShowPausedModal(true)}
                    companyWorkflowStatus={company.workflowStatus}
                    hasJobs={jobs.length > 0 ? true : false}
                  />
                ) : (
                  <JobCounts visibleCount={company.totalJobs} />
                ))}
            </LeftSideWrapper>
            {(createJobFeature?.message === "paused" ||
              createJobFeature?.granted) &&
              !jobSlotsFeature?.granted && (
                <CreateJobButton
                  data-testid="create-job-button"
                  level="secondary"
                  onClick={() => {
                    if (createJobFeature?.message === "paused") {
                      setShowPausedModal(true);
                    } else {
                      navigate("./create");
                    }
                  }}
                >
                  <Icon icon="plus" size={-1} />
                  <Text bold size={-1}>
                    Create
                  </Text>
                </CreateJobButton>
              )}
          </TopSectionContainer>
          <JobsListContainer>
            {loading || featuresLoading || !company ? (
              <Loading />
            ) : error ? (
              <ReloadableError action={refetch} />
            ) : jobSlotsFeature?.granted &&
              jobs.length &&
              status === "published" ? (
              <CoreJobList jobs={jobs} review={false} />
            ) : jobs.length ? (
              <InnerJobsList
                jobs={jobs}
                review={status === "review"}
                isPublished={status === "published"}
              />
            ) : viewJobsFeature?.message === "paused" ? (
              <PausedProUpsellPage campaign="jobs" />
            ) : (
              <JobsListEmptyState />
            )}
          </JobsListContainer>
        </FlexContainer>
      </JobsPageContainer>
      <RemovedJobsExplanation enabled={showPausedModal} campaign="jobs" />
      <Outlet />
    </>
  );
};
